/**
 * Shared store-interaction core for the two publish front-ends — the curator
 * sidecar flow (`source manifest publish`) and the first-party library flow
 * (`library publish`). Both differ only in their git side-effects + how they
 * resolve the source repo; the store orchestration below (register → sync →
 * candidates → per-asset preview+publish) is identical.
 *
 * The store REST surface is reached through the injectable {@link PublisherClient}
 * so the orchestration stays unit/integration-testable without a live store.
 *
 * @docLink cli/commands/source#manifest-publish
 */

import type { SkPublishManifest } from "@skaile/workspaces/core";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";
import type { PublisherClient } from "../store-publisher.ts";

/**
 * One-line rendering of a namespace-availability probe for a `--dry-run` plan.
 * The `message` is store-authored (identical to what a real publish would echo)
 * — we only prepend a status glyph so the reader sees green/amber/red at a
 * glance without the CLI re-deriving any authorization logic.
 */
export function formatNamespaceAvailability(a: {
  status: "owned-by-you" | "claimable-by-you" | "reserved" | "available";
  message: string;
}): string {
  switch (a.status) {
    case "owned-by-you":
    case "available":
      return `${S.ok("✓")} ${a.message}`;
    case "claimable-by-you":
      return `${S.warn("!")} ${a.message}`;
    default:
      return `${S.err("✗")} ${a.message}`;
  }
}

/**
 * Preflight the target namespace against the store's availability probe BEFORE a
 * publish does any irreversible work (a public git push). Returns `{ ok: false }`
 * with a guided message for `reserved` (not yours) and `claimable-by-you` (must
 * claim first) — the two states a real publish would reject — so the caller can
 * abort with actionable guidance instead of a raw 422 after pushing bytes.
 *
 * The probe is advisory: any failure (offline, older store) returns `{ ok: true }`
 * so the store stays the authority and a valid publish is never blocked.
 */
export async function preflightNamespace(
  client: PublisherClient,
  namespace: string,
): Promise<{ ok: boolean; message?: string }> {
  let a: Awaited<ReturnType<PublisherClient["namespaceAvailability"]>>;
  try {
    a = await client.namespaceAvailability(namespace);
  } catch {
    return { ok: true };
  }
  switch (a.status) {
    case "owned-by-you":
    case "available":
      return { ok: true };
    case "claimable-by-you":
      return {
        ok: false,
        message:
          `@${namespace} isn't yours yet — claim it in the store first` +
          `${a.claimMethod ? ` (${a.claimMethod})` : ""}, then re-run. ${a.message}`,
      };
    default: // reserved, or any unrecognized future status (fail safe → block)
      return { ok: false, message: `Cannot publish under @${namespace} — ${a.message}` };
  }
}

/** One candidate row shape the matcher operates on (kind + name are the key). */
interface CandidateLike {
  kind: string;
  name: string;
}

/**
 * Keep only candidates whose `(kind, name)` pair is declared in the manifest.
 * The backend may merge extra assets from the base repo into the candidate
 * list; a publish only touches what the manifest curated/declared.
 */
export function matchCandidatesToManifest<T extends CandidateLike>(
  candidates: T[],
  manifest: SkPublishManifest,
): T[] {
  const wanted = new Set((manifest.assets ?? []).map((a) => `${a.kind}:${a.name}`));
  return candidates.filter((c) => wanted.has(`${c.kind}:${c.name}`));
}

/** A canonical ref string `kind:@publisher/name[#version]` for reporting. */
export function refFor(kind: string, publisher: string, name: string, version?: string): string {
  const base = `${kind}:@${publisher}/${name}`;
  return version ? `${base}#${version}` : base;
}

/** The bare asset name from a `kind:@publisher/name#version` ref or a plain name. */
export function bareName(ref: string): string {
  const afterKind = ref.includes(":") ? ref.slice(ref.indexOf(":") + 1) : ref;
  const afterScope = afterKind.includes("/")
    ? afterKind.slice(afterKind.indexOf("/") + 1)
    : afterKind;
  return afterScope.split("#")[0];
}

/**
 * Poll a job until it leaves `running` or the deadline passes. Returns the last
 * observed state — the caller MUST treat a still-`running` result as a timeout
 * (not success), since it means the deadline was hit before the job finished.
 */
export async function pollJob(
  client: PublisherClient,
  jobId: string,
  intervalMs = 1_500,
  timeoutMs = 60_000,
): Promise<{ status: string; items?: Array<{ label: string; status: string; error?: string }> }> {
  const deadline = Date.now() + timeoutMs;
  let job = await client.jobGet(jobId);
  while (job.status === "running" && Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, intervalMs));
    job = await client.jobGet(jobId);
  }
  return job;
}

/**
 * True when a `sourcesAdd` rejection is a duplicate-URL conflict (the source is
 * already registered) — the only error the publish flow recovers from by looking
 * the existing row up. Every other failure (auth, 5xx, network) must propagate.
 */
export function isDupSourceError(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase();
  return /\b409\b|conflict|duplicate|already exists|already registered|unique/.test(msg);
}

/**
 * Register + sync the source, then publish its curated/declared candidates —
 * the store-side steps shared by both publish front-ends. Git side-effects (the
 * sidecar push, or the library commit+push) happen in the caller *before* this;
 * this function is purely store interaction.
 *
 * The sidecar front-end passes `sidecar` (so `sourcesAdd` carries the sidecar
 * pin); the library front-end omits it (the source repo IS the manifest host).
 * `assetName`, when set, narrows publication to the single candidate of that
 * name. Fatal store states (`sourcesAdd` non-dup failure) surface; sync failures
 * and empty candidate sets `process.exit(1)` with the same messages as before.
 */
export async function publishToStore(opts: {
  sourceUrl: string;
  ref: string;
  sidecar?: { url: string; ref: string };
  manifest: SkPublishManifest;
  assetName?: string;
  client: PublisherClient;
}): Promise<{ published: string[] }> {
  const { sourceUrl, ref, sidecar, manifest, assetName, client: pub } = opts;

  let sourceId: string;
  try {
    const added = await pub.sourcesAdd({
      url: sourceUrl,
      ref,
      ...(sidecar ? { sidecarUrl: sidecar.url, sidecarRef: sidecar.ref } : {}),
      // Publish under the manifest's declared namespace; the store resolves a
      // claimed Publisher when it differs from the caller's login, else defaults
      // to `@<username>`. Omitted when the manifest carries no publisher.
      ...(manifest.publisher ? { namespace: manifest.publisher } : {}),
    });
    sourceId = added.id;
  } catch (err) {
    // Only a duplicate-url conflict is recoverable (source already registered);
    // auth/5xx/network failures must surface, not silently reuse a stale row.
    if (!isDupSourceError(err)) throw err;
    const rows = await pub.sourcesList();
    const found = rows.find((r) => r.url === sourceUrl);
    if (!found) {
      throw new Error(
        `Source ${sourceUrl} looked already-registered but no owned row matched: ` +
          `${err instanceof Error ? err.message : String(err)}`,
      );
    }
    sourceId = found.id;
  }

  const { jobId } = await pub.sourceSync(sourceId);
  const job = await pollJob(pub, jobId);
  if (job.status === "failed" || job.status === "cancelled") {
    const failed = (job.items ?? []).filter((i) => i.status === "failed");
    for (const i of failed) logErr(`sync: ${i.label}: ${i.error ?? "failed"}`);
    logErr(`Source sync ${job.status}.`);
    process.exit(1);
  }
  // A still-`running` job means the poll deadline was hit — do NOT proceed on an
  // unfinished sync (it would read incomplete candidates).
  if (job.status === "running") {
    logErr("Source sync timed out before completing — re-run `publish`, or check the store.");
    process.exit(1);
  }

  let candidates = matchCandidatesToManifest(await pub.sourceCandidates(sourceId), manifest);
  if (assetName) candidates = candidates.filter((c) => c.name === assetName);
  if (candidates.length === 0) {
    logWarn("No candidates discovered — check the manifest and the source ref.");
    process.exit(1);
  }

  const published: string[] = [];
  for (const c of candidates) {
    try {
      const preview = await pub.assetPreview(c.id);
      const res = await pub.assetPublish(c.id, { ref, files: preview.discoveredFiles });
      // Publish is async — wait for the job so we only report refs that actually
      // landed, not ones whose import is still in flight or about to fail.
      if (res.jobId) {
        const pubJob = await pollJob(pub, res.jobId);
        if (pubJob.status !== "succeeded") {
          const errs = (pubJob.items ?? [])
            .filter((i) => i.status === "failed")
            .map((i) => i.error)
            .filter(Boolean);
          logWarn(
            `Publish of ${c.kind}:${c.name} did not succeed (${pubJob.status})` +
              (errs.length ? `: ${errs.join("; ")}` : ""),
          );
          continue;
        }
      }
      published.push(refFor(c.kind, preview.candidate.publisherNamespace, c.name, c.version));
    } catch (err) {
      logWarn(
        `Failed to publish ${c.kind}:${c.name}: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  logOk(`Published ${published.length} asset(s):`);
  for (const r of published) logInfo(`  ${r}`);
  return { published };
}
